import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaInstagram, FaTelegramPlane, FaTiktok, FaWhatsapp } from "react-icons/fa";

const socialLinks = [
  { label: "Telegram", href: "https://t.me/matchespro", icon: FaTelegramPlane },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@profitmakertool?_r=1&_t=ZS-95oLSw4DVxo",
    icon: FaTiktok,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/markethunter.site?igsh=MWx0NXNxa3ljOXZzaA%3D%3D&utm_source=qr",
    icon: FaInstagram,
  },
  {
    label: "WhatsApp",
    href: "https://chat.whatsapp.com/BiM2rqbgAzJ1yBOE5h6E8p",
    icon: FaWhatsapp,
  },
];

const features = [
  {
    title: "Three dedicated tools",
    desc: "Over/Under, Even/Odd, and Matches — each built specifically for its contract type instead of trying to cover everything at once.",
  },
  {
    title: "Live confidence guidance",
    desc: "Every alert comes with a clear confidence readout so you can filter for cleaner opportunities.",
  },
  {
    title: "Fast entry timing",
    desc: "Countdown-driven timing helps you approach entries with more structure and less guesswork.",
  },
  {
    title: "Secure Deriv connection",
    desc: "Official OAuth sign-in keeps your Deriv credentials with Deriv. No password storage on our side.",
  },
];

const steps = [
  ["Connect", "Sign in with your Deriv account using the approved Matches Pro app connection."],
  ["Choose", "Pick the tool that fits your trading style — Over/Under, Even/Odd, or Matches."],
  ["Execute", "Take the opportunity that fits your plan while the signal window is active."],
];

const pricingTiers = [
  {
    id: "overUnder",
    name: "Over/Under Pro",
    price: "$70",
    description: "Entry-level predictions for Over/Under last-digit contracts",
    features: [
      "Live Over/Under signal monitoring",
      "Confidence scoring per index",
      "All 13 volatility indices",
      "Countdown-driven entry timing",
      "Secure Deriv OAuth connection",
      "USDT TRC20: TYGre7SuTUeT6XYw68W9tiPVWFAAjPhLAB",
      "After payment, contact: t.me/Profitmakertool",
    ],
    highlight: false,
  },
  {
    id: "evenOdd",
    name: "Even/Odd Pro",
    price: "$100",
    description: "Precision Even/Odd signals for last-digit contracts",
    features: [
      "Live Even/Odd signal monitoring",
      "Confidence scoring per index",
      "All 13 volatility indices",
      "Countdown-driven entry timing",
      "Secure Deriv OAuth connection",
      "USDT TRC20: TYGre7SuTUeT6XYw68W9tiPVWFAAjPhLAB",
      "After payment, contact: t.me/Profitmakertool",
    ],
    highlight: false,
  },
  {
    id: "matches",
    name: "Matches Pro",
    price: "$200",
    description: "Full-featured exact digit prediction for Matches contracts",
    features: [
      "Live last-digit Matches monitoring",
      "Signal confidence and entry windows",
      "Coverage across all volatility indices",
      "Secure Deriv OAuth connection",
      "USDT TRC20: TYGre7SuTUeT6XYw68W9tiPVWFAAjPhLAB",
      "After payment, contact: t.me/Profitmakertool",
    ],
    highlight: true,
  },
];

const faqs = [
  {
    q: "What tools are available?",
    a: "Matches Pro offers three tools: Over/Under ($70), Even/Odd ($100), and Matches ($200). Each tool is purchased independently — buy one, two, or all three.",
  },
  {
    q: "How much does access cost?",
    a: "Over/Under is $70 USD, Even/Odd is $100 USD, and Matches is $200 USD. All are one-time payments.",
  },
  {
    q: "Which markets does it support?",
    a: "All three tools track the major Deriv volatility indices, including 1s and standard index variants.",
  },
  {
    q: "Is my Deriv account secure?",
    a: "Yes. Access uses Deriv OAuth, which means your sign-in happens through Deriv directly and your password is not stored by Matches Pro.",
  },
  {
    q: "Does the tool guarantee wins?",
    a: "No. Trading always carries risk. Matches Pro is designed to help you identify structured opportunities, not eliminate market risk.",
  },
  {
    q: "Where can I reach support or the community?",
    a: "Use the official Matches Pro channels on this page. After payment, contact Telegram at t.me/Profitmakertool for confirmation.",
  },
];

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasDerivAuthCallback =
      params.has("acct1") ||
      params.has("token1") ||
      params.has("code") ||
      params.has("error");

    if (hasDerivAuthCallback) {
      navigate(`/login${location.search}`, { replace: true });
    }
  }, [location, navigate]);

  return (
    <div className="min-h-screen overflow-hidden bg-[#0f1514] text-white">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(129,156,151,0.22),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(168,127,35,0.18),transparent_35%)]" />
      </div>

      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#0f1514]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.jpeg" alt="Matches Pro" className="h-10 w-10 rounded-full border border-white/10 object-cover" />
            <div>
              <p className="text-lg font-black tracking-[0.18em] text-[#A87F23]">MATCHES PRO</p>
              <p className="text-xs uppercase tracking-[0.28em] text-[#819C97]">Deriv Signals</p>
            </div>
          </Link>

          <div className="hidden items-center gap-6 text-sm text-slate-200 md:flex">
            <a href="#features" className="transition hover:text-[#A87F23]">Features</a>
            <a href="#how-it-works" className="transition hover:text-[#A87F23]">How It Works</a>
            <a href="#pricing" className="transition hover:text-[#A87F23]">Pricing</a>
            <a href="#community" className="transition hover:text-[#A87F23]">Community</a>
            <Link
              to="/login"
              className="rounded-full border border-[#A87F23]/60 bg-[#A87F23] px-5 py-2 font-semibold text-[#0f1514] transition hover:bg-[#c19432]"
            >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      <main className="relative">
        <section className="mx-auto grid max-w-6xl gap-12 px-6 pb-24 pt-20 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
          <div>
            <p className="mb-4 inline-flex rounded-full border border-[#819C97]/40 bg-[#819C97]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-[#819C97]">
              Precision-focused trading toolkit
            </p>
            <h1 className="max-w-1xl text-1xl font-black leading-tight text-white md:text-6xl">
              Matches Pro helps traders spot cleaner opportunities with more timing discipline.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-7 text-slate-300 md:text-lg">
              Three dedicated tools for Deriv last-digit contracts — Over/Under, Even/Odd, and Matches.
              Track live signals, review confidence, and move with a simple process instead of chasing noise.
            </p>

            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/login"
                className="rounded-full bg-[#A87F23] px-8 py-4 text-center font-bold text-[#0f1514] shadow-lg shadow-[#A87F23]/20 transition hover:bg-[#c19432]"
              >
                Get Access from $70
              </Link>
              <a
                href="#pricing"
                className="rounded-full border border-[#819C97]/40 px-8 py-4 text-center font-semibold text-[#d6dedc] transition hover:border-[#819C97] hover:bg-white/5"
              >
                See all tools
              </a>
            </div>

            <div className="mt-12 grid gap-4 sm:grid-cols-3">
              {[
                ["3 Tools", "Over/Under, Even/Odd, Matches"],
                ["Live Timing", "Countdown-driven setup flow"],
                ["From $70", "Flexible per-tool pricing"],
              ].map(([title, text]) => (
                <div key={title} className="rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur">
                  <p className="text-2xl font-black text-[#A87F23]">{title}</p>
                  <p className="mt-2 text-sm text-slate-300">{text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="rounded-[2rem] border border-[#819C97]/25 bg-[#17201f]/90 p-6 shadow-2xl shadow-black/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#819C97]">Live Panel</p>
                  <p className="mt-2 text-2xl font-black text-white">Matches Pro</p>
                </div>
              </div>

              <div className="mt-8 rounded-[1.75rem] border border-white/10 bg-[#0f1514] p-6">
                <div className="flex items-center justify-between text-sm text-slate-400">
                  <span>Volatility 100 (1s)</span>
                  <span className="text-[#819C97]">Live scanning</span>
                </div>

                <div className="mt-8 text-center">
                  <p className="text-sm uppercase tracking-[0.28em] text-[#819C97]">Active call</p>
                  <p className="mt-3 text-6xl font-black text-[#A87F23]">6</p>
                  <p className="mt-3 text-lg font-semibold text-white">Matches setup window</p>
                </div>

                <div className="mt-8 grid grid-cols-2 gap-4">
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Confidence</p>
                    <p className="mt-2 text-3xl font-black text-[#819C97]">79%</p>
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Entry window</p>
                    <p className="mt-2 text-3xl font-black text-[#819C97]">00:08</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="features" className="border-y border-white/10 bg-[#131b1a]/80 py-20">
          <div className="mx-auto max-w-6xl px-6">
            <div className="mb-12 max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#819C97]">Why Matches Pro</p>
              <h2 className="mt-3 text-3xl font-black text-white md:text-4xl">Three focused tools. One clean workflow.</h2>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {features.map((feature, index) => (
                <div key={feature.title} className="rounded-[2rem] border border-white/10 bg-white/5 p-7">
                  <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-[#A87F23]/15 font-black text-[#A87F23]">
                    {index + 1}
                  </div>
                  <h3 className="text-2xl font-bold text-white">{feature.title}</h3>
                  <p className="mt-3 leading-7 text-slate-300">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="mx-auto max-w-6xl px-6 py-20">
          <div className="mb-12 text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#819C97]">Workflow</p>
            <h2 className="mt-3 text-3xl font-black text-white md:text-4xl">How Matches Pro fits into your setup</h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {steps.map(([title, desc], index) => (
              <div key={title} className="rounded-[2rem] border border-white/10 bg-[#17201f]/80 p-8">
                <p className="text-5xl font-black text-[#A87F23]/85">{`0${index + 1}`}</p>
                <h3 className="mt-6 text-2xl font-bold text-white">{title}</h3>
                <p className="mt-4 leading-7 text-slate-300">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="pricing" className="bg-[#131b1a]/80 py-20">
          <div className="mx-auto max-w-6xl px-6">
            <div className="mb-12 text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#819C97]">Pricing</p>
              <h2 className="mt-3 text-3xl font-black text-white md:text-4xl">One-time access per tool — buy what you need</h2>
              <p className="mx-auto mt-5 max-w-2xl leading-7 text-slate-300">
                Each tool is sold separately. Pick one, pick two, or get all three.
                All payments are one-time via USDT TRC20.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {pricingTiers.map((tier) => (
                <div
                  key={tier.id}
                  className={`relative flex flex-col rounded-[2.25rem] p-8 shadow-2xl shadow-black/20 ${
                    tier.highlight
                      ? "border-2 border-[#A87F23]/60 bg-[#17201f]"
                      : "border border-white/10 bg-[#17201f]"
                  }`}
                >
                  {tier.highlight && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="rounded-full bg-[#A87F23] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#0f1514]">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#819C97]">
                      {tier.name}
                    </p>
                    <p className={`mt-3 text-5xl font-black ${tier.highlight ? "text-[#A87F23]" : "text-white"}`}>
                      {tier.price}
                    </p>
                    <p className="mt-3 text-sm leading-6 text-slate-400">{tier.description}</p>
                  </div>

                  <div className="mt-8 flex-1 space-y-2">
                    {tier.features.map((item) => (
                      <div key={item} className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-slate-200">
                        {item}
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/login"
                    className={`mt-8 block rounded-full py-3.5 text-center font-bold transition ${
                      tier.highlight
                        ? "bg-[#A87F23] text-[#0f1514] hover:bg-[#c19432]"
                        : "border border-[#819C97]/40 text-white hover:border-[#819C97] hover:bg-white/5"
                    }`}
                  >
                    Get {tier.name}
                  </Link>
                </div>
              ))}
            </div>

            <p className="mt-8 text-center text-sm text-slate-500">
              All payments via USDT TRC20 · Contact Telegram after payment for activation
            </p>
          </div>
        </section>

        <section id="community" className="mx-auto max-w-6xl px-6 py-20">
          <div className="mb-10 text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#819C97]">Official Links</p>
            <h2 className="mt-3 text-3xl font-black text-white md:text-4xl">Find Matches Pro across the community channels</h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {socialLinks.map(({ label, href, icon: Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-4 rounded-[1.75rem] border border-white/10 bg-white/5 p-5 transition hover:border-[#819C97]/50 hover:bg-white/10"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#819C97]/15 text-xl text-[#819C97]">
                  <Icon />
                </div>
                <div>
                  <p className="font-bold text-white">{label}</p>
                  <p className="text-sm text-slate-400">Open official Matches Pro channel</p>
                </div>
              </a>
            ))}
          </div>
        </section>

        <section className="border-t border-white/10 bg-[#131b1a]/80 py-20">
          <div className="mx-auto max-w-4xl px-6">
            <div className="mb-10 text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-[#819C97]">FAQ</p>
              <h2 className="mt-3 text-3xl font-black text-white md:text-4xl">Common questions about Matches Pro</h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq) => (
                <details key={faq.q} className="rounded-[1.5rem] border border-white/10 bg-white/5 p-6">
                  <summary className="cursor-pointer list-none text-lg font-semibold text-white">{faq.q}</summary>
                  <p className="mt-4 leading-7 text-slate-300">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 bg-[#0f1514] py-10">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 text-sm text-slate-400 md:flex-row md:items-center md:justify-between">
          <p>© 2026 Matches Pro. Trading carries risk and requires personal judgment.</p>
          <div className="flex flex-wrap gap-4">
            {socialLinks.map(({ label, href }) => (
              <a key={label} href={href} target="_blank" rel="noreferrer" className="transition hover:text-[#A87F23]">
                {label}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
